
import { AutocompleteProps, InputAdornment, Stack, TextField } from "@mui/material";
import { MyLocation, Place } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useLocationField } from "components/layout/event/new/hooks/useLocationField";
import { SearchSelect } from "components/layout/forms/fields/Autocomplete/SearchSelect";
import { useFeatureFlags } from "components/providers/featureFlags/useFeatureFlags";
import { useLocalizationContext, useTranslate } from "components/providers/LocalizationProvider";
import { Location } from "graphql/generated/types";
import { useFirstRender } from "hooks/useFirstRender";
import { useEffect, useState } from "react";
import { useLazyGeolocation } from "components/search/useLazyGeolocation";
import { trackClick, trackingCategory } from "utils/tracking";
import { useError } from "hooks/useNotifications";

type RawPlacePrediction = {
    placeId: string;
    text: { toString(): string };
    toPlace(): { fetchFields(): Promise<any> };
}

export const LocationSearch: React.FC<{ onChange?: (value: Location | null) => void }> = ({ onChange }) => {
    const [data, setData] = useState<Location | null>(null);
    const [location, setLocation] = useState<{label: string, value: RawPlacePrediction} | Location | null>(null);
    const geolocation = useLazyGeolocation();
    const { options, autocompleteProps: baseAutocompleteProps, loading } = useLocationField({
        value: (location as any)?.value || "",
        dateField: null,
        getValue: (value: any) => value,
        getInitialValueLabel: (value: any) => value?.placePredictionText || value?.text?.toString() || "",
        setValue: setData,
        name: "location"
    });

    const isFirstRender = useFirstRender();
    useEffect(() => {
        if (isFirstRender) return;

        onChange?.(data);
    }, [data]);

    const { notifyError } = useError();
    const { translate } = useLocalizationContext();
    const placeholder = useTranslate("Any location");
    const currentLocationText = useTranslate("Current location");
    const isLocationSearchEnabled = useFeatureFlags("Location Search");
    if (!isLocationSearchEnabled) return null;

    const autocompleteProps: Omit<AutocompleteProps<any, false, false, boolean, "div">, "options"> = {
        ...baseAutocompleteProps,
        blurOnSelect: true,
        clearOnBlur: false,
        getOptionLabel: (option: any) => {
            return option?.label || option?.placePredictionText || option?.text?.toString() || "";
        },
        renderOption: Option,
        renderInput: (params: any) => {
            return <TextField
                id={params.id}
                fullWidth={params.fullWidth}
                placeholder={placeholder}
                onClick={trackClick("Location search input", trackingCategory.searchEventAndOrganisation)}
                slotProps={{
                    input: {
                        ...params.InputProps,
                        startAdornment: <InputAdornment position="start" sx={{ mr: 0, color: "text.primary" }}><Place /></InputAdornment>,
                    },
                    htmlInput: { ...params.inputProps }
                }}/>;
        }
    };
    const fieldOptions = navigator.geolocation ? [{ label: currentLocationText, value: "currentLocation", type: "currentLocation" }, ...options] : options;
    const field = {
        name: "location",
        loading,
        options: fieldOptions,
        placeholder,
        autocompleteProps
    };

    const input = {
        value: "location",
        onChange: (v: any) => {
            if (v?.type === "currentLocation") {
                (async () => {
                    try {
                        const coords = await geolocation?.update();
                        if (coords) {
                            setData({
                                addressType: "route",
                                placePredictionText: currentLocationText,
                                formattedAddress: currentLocationText,
                                googleMapsUri: "",
                                id: "currentLocation",
                                utcOffsetMinutes: 0,
                                location: coords
                            });
                        } else {
                            setData(null);
                        }} catch (_error) {
                        notifyError(translate("Sorry, we couldn't find your current location right now."));
                    }
                })();
            } else {
                setLocation(v);
            }
        }
    };

    return <SearchSelect field={field} input={input} />;
};

const Option = (props: React.ComponentProps<"li">, option: { label: string, value: unknown, type?: "currentLocation" }) => {
    return <li {...props} key={option.label}>
        <Box display="flex" justifyContent="space-between" width="100%" sx={{ wordBreak: "break-word" }} >
            <Stack direction="row" spacing={1} alignItems="center">
                {option.type === "currentLocation" ? <MyLocation fontSize='small' /> : null}
                <div>{option.label}</div>
            </Stack>
        </Box>
    </li>;
};
